import { drybulkCargoOperationsService } from "@services/drybulkCargoOperationsApi";
import { getMessages } from "@services/messageApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import {
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  GRID_REFETCH_INTERVAL_MS,
  MESSAGE_ENTITY_TYPE,
} from "../constants";
import { usePollingGate } from "./usePollingGate";

const OPEN_THREAD_INQUIRY_ID = "drybulk_cargo_operations.messages.thread";

export const useCargoMessages = (
  operationId: string,
  threadKey: string | undefined,
  { sending = false }: { sending?: boolean } = {},
) => {
  const { busy } = usePollingGate();
  const enabled = Boolean(operationId && threadKey);
  const polling = {
    enabled,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: busy || sending ? false : GRID_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  } as const;

  const messages = useQuery({
    ...polling,
    queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.messages(operationId, threadKey ?? ""),
    queryFn: () =>
      getMessages({ entityId: operationId, entityType: MESSAGE_ENTITY_TYPE, threadKey }),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === threadKey ? previous : undefined,
  });
  const files = useQuery({
    ...polling,
    queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.messageAttachments(operationId, threadKey ?? ""),
    queryFn: () => drybulkCargoOperationsService.listMessageAttachments(operationId, threadKey),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === threadKey ? previous : undefined,
  });

  useTrackPendingInquiry(OPEN_THREAD_INQUIRY_ID, {
    isFetching: messages.isFetching,
    isLoading: messages.isLoading,
    isError: messages.isError,
    isSuccess: messages.isSuccess,
    error: messages.error,
    dataUpdatedAt: messages.dataUpdatedAt,
  });

  const trackThreadSwitch = () =>
    startInquiryTiming(OPEN_THREAD_INQUIRY_ID, {
      module: DRYBULK_CARGO_OPS_TELEMETRY.module,
      screen: DRYBULK_CARGO_OPS_TELEMETRY.messagesScreen,
      action: "open_message_thread",
      entityType: "cargo_operation",
    });

  return {
    messages: messages.data,
    files: files.data,
    isLoading: enabled && messages.isLoading,
    isError: messages.isError,
    isFilesError: files.isError,
    refetchFiles: files.refetch,
    trackThreadSwitch,
  };
};
